import React, { useEffect } from "react";
import { connect } from "react-redux";

import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import WorkspacePremiumOutlinedIcon from "@mui/icons-material/WorkspacePremiumOutlined";
import QuizOutlinedIcon from "@mui/icons-material/QuizOutlined";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import ManageAccountsOutlinedIcon from "@mui/icons-material/ManageAccountsOutlined";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";

import classes from "./styles.module.scss";
import SideItem from "./sideItems";
import { importAdminDataFromLocal } from "../../../store/auth/actions";

// Icon per navigation group, keyed by the group's `checkValue`.
const GROUP_ICONS = {
  dashboard: DashboardOutlinedIcon,
  candidates: GroupsOutlinedIcon,
  exams: AssignmentOutlinedIcon,
  certificates: WorkspacePremiumOutlinedIcon,
  questions: QuizOutlinedIcon,
  special_slots: EventAvailableOutlinedIcon,
  users: ManageAccountsOutlinedIcon,
  cadre: AccountTreeOutlinedIcon,
  changePassword: LockOutlinedIcon,
  all: LogoutOutlinedIcon,
};

// Groups are ordered by how often they are used day to day, not
// alphabetically — reporting and candidate triage sit at the top.
const NAV_ITEMS = [
  {
    checkValue: "dashboard",
    name: "Dashboard",
    links: [
      { title: "Dashboard", to: "/dashboard" },
      { title: "Report", to: "/report" },
    ],
  },
  {
    checkValue: "candidates",
    name: "Candidates",
    links: [{ title: "Candidates", to: "/view-students" }],
  },
  {
    checkValue: "exams",
    name: "Exams",
    links: [
      { title: "All Exams", to: "/view-exams" },
      { title: "BOR Migrated", to: "/bor-migrated" },
      { title: "Waitlist", to: "/waitlist" },
    ],
  },
  {
    checkValue: "certificates",
    name: "Certificates",
    links: [{ title: "Certificates", to: "/certificates" }],
  },
  {
    checkValue: "questions",
    name: "Question Bank",
    links: [{ title: "Subject", to: "/subject" }],
  },
  {
    checkValue: "special_slots",
    name: "Special Slots",
    links: [{ title: "Special Slots", to: "/special-slots" }],
  },
  {
    checkValue: "users",
    name: "Admin Users",
    links: [{ title: "New Users", to: "/view-users" }],
  },
  {
    checkValue: "cadre",
    name: "Service Cadre",
    links: [{ title: "Cadre", to: "/view-cadre" }],
  },
  {
    checkValue: "changePassword",
    name: "Change Password",
    links: [{ title: "Change Password", to: "/change-password" }],
  },
];

const LOGOUT_ITEM = { checkValue: "all", name: "Logout", to: "/logout" };

// Which heading each group sits under. Groups the admin has no permission for
// drop out first, and a heading with nothing under it is not rendered.
const SECTIONS = [
  { title: "Overview", groups: ["dashboard"] },
  { title: "Operations", groups: ["candidates", "exams", "special_slots", "certificates"] },
  { title: "Administration", groups: ["questions", "users", "cadre", "changePassword"] },
];

// Keeps only the groups (and links within them) the signed-in admin has been
// granted. Matching is on the section name, exactly as the backend returns it.
export function generateSidebarMenu(permissionData) {
  return NAV_ITEMS.map((navItem) => {
    const filteredLinks = navItem.links?.filter((link) =>
      permissionData?.some(
        (permission) =>
          permission.section_name?.toUpperCase() === link.title.toUpperCase()
      )
    );

    return filteredLinks?.length > 0 ? { ...navItem, links: filteredLinks } : null;
  }).filter(Boolean);
}

const LeftBar = (props) => {
  // App already restores the session on mount. Dispatching it again here
  // produced a second `authDetails` object, which changed the dependency of
  // the route-building effect in PagesRoute, rebuilt the route array, and
  // remounted the current page — so every screen fetched its data twice.
  const { collapsed, mobileOpen } = props;
  const sidebarMenu = generateSidebarMenu(props.auth.authDetails.assignedSections);
  const admin = props.auth.authDetails?.data;

  return (
    <aside
      className={[
        classes.sidebar,
        collapsed ? classes.collapsed : "",
        mobileOpen ? classes.mobile_open : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <div className={classes.brand}>
        <div className={classes.brand_mark}>BOR</div>
        <div className={classes.brand_text}>
          <span className={classes.brand_name}>BOR Admin</span>
          <span className={classes.brand_sub}>Examination Portal</span>
        </div>
      </div>

      <nav className={classes.nav}>
        {SECTIONS.map((section) => {
          const groups = sidebarMenu.filter((g) =>
            section.groups.includes(g.checkValue)
          );
          if (!groups.length) return null;

          return (
            <div key={section.title} className={classes.nav_section}>
              <p className={classes.nav_label}>{section.title}</p>
              {groups.map((navItem) => (
                <SideItem
                  key={navItem.checkValue}
                  items={navItem}
                  icon={GROUP_ICONS[navItem.checkValue]}
                  collapsed={collapsed}
                  onNavigate={props.onNavigate}
                />
              ))}
            </div>
          );
        })}

        <div className={classes.nav_divider} />

        <SideItem
          items={LOGOUT_ITEM}
          to={LOGOUT_ITEM.to}
          icon={GROUP_ICONS.all}
          collapsed={collapsed}
          onNavigate={props.onNavigate}
        />
      </nav>

      <div className={classes.footer}>
        <div className={classes.avatar}>
          {(admin?.username || admin?.user_type || "A").charAt(0).toUpperCase()}
        </div>
        <div className={classes.footer_text}>
          <span className={classes.footer_name}>
            {admin?.username || "Administrator"}
          </span>
          <span className={classes.footer_role}>{admin?.user_type || "ADMIN"}</span>
        </div>
      </div>
    </aside>
  );
};

const mapStateToProps = (state) => {
  return {
    auth: state.Auth,
  };
};

const mapDispatchToProps = { importAdminDataFromLocal };

export default connect(mapStateToProps, mapDispatchToProps)(LeftBar);
